/**
 * Quotation Display Configuration
 * 
 * Centralized display rules and utilities for consistent quotation rendering
 * across all tabs (Custom, DMC, Manual) and views (Preview, Detail, PDF).
 * 
 * Usage:
 *   import { PRICING_COLUMNS, shouldHideGrandTotal, formatCurrency } from './displayConfig';
 */

// =============================================================================
// PRICING TABLE CONFIGURATION
// =============================================================================

/**
 * Standard column order for pricing tables
 * All pricing tables should follow this sequence
 */
export const PRICING_COLUMN_ORDER = [
  'category',      // Option/Category name (always first)
  'adult',         // Adult per-person price
  'extra_bed',     // Extra bed/adult price
  'child_with_bed',// Child with bed price
  'child_no_bed',  // Child without bed price  
  'subtotal',      // Subtotal before GST
  'gst',           // GST amount
  'total'          // Final total
];

/**
 * Column header labels with pax count placeholders
 */
export const PRICING_COLUMN_LABELS = {
  category: 'Category',
  adult: 'Adult',
  extra_bed: 'Extra Bed',
  child_with_bed: 'Child w/Bed',
  child_no_bed: 'Child w/o Bed',
  subtotal: 'Subtotal',
  gst: 'GST',
  total: 'Total'
};

/**
 * Determine which columns should be visible based on pax data
 * Columns with 0 pax are hidden to keep the table clean
 */
export const getVisiblePricingColumns = (paxData, pricingOptions = []) => {
  const { adultsInRooms = 0, extraAdults = 0, childrenWithBed = 0, childrenNoBed = 0 } = paxData;
  
  // Check if any pricing option has non-zero values for optional columns
  const hasExtraBedPricing = pricingOptions.some(opt => (opt.extra_adult || 0) > 0);
  const hasChildWBPricing = pricingOptions.some(opt => (opt.child_with_bed || 0) > 0);
  const hasChildNBPricing = pricingOptions.some(opt => (opt.child_no_bed || 0) > 0);
  
  return {
    category: true,
    adult: true, // Always show adult column
    extra_bed: (hasExtraBedPricing || extraAdults > 0) && extraAdults > 0,
    child_with_bed: (hasChildWBPricing || childrenWithBed > 0) && childrenWithBed > 0,
    child_no_bed: (hasChildNBPricing || childrenNoBed > 0) && childrenNoBed > 0,
    subtotal: true,
    gst: true,
    total: true
  };
};

// =============================================================================
// GRAND TOTAL VISIBILITY RULES
// =============================================================================

/**
 * Determine if Grand Total section should be hidden
 * 
 * Rules:
 * - Hide for multi-category quotes (customer picks ONE)
 * - Hide for multi-meal-plan quotes (customer picks ONE)
 * - Hide for DMC packages with multiple options
 * 
 * @param {Object} quotation - The quotation object
 * @param {Object} dmcParsed - Parsed DMC content (if DMC package)
 * @returns {boolean} - true if Grand Total should be hidden
 */
export const shouldHideGrandTotal = (quotation, dmcParsed = {}) => {
  const numCategories = (quotation.selected_categories || []).length || 1;
  const numMealPlans = Object.keys(quotation.quote_line_items?.meal_plan_pricing || {}).length || 
                       (quotation.selected_meal_plans || []).length || 1;
  
  // For DMC packages, check pricing/accommodation options
  const isDMC = quotation.route_type === 'dmc_package';
  const dmcNumOptions = isDMC 
    ? Math.max(
        dmcParsed.pricing_options?.length || 0,
        dmcParsed.accommodation_options?.length || 0
      )
    : 1;
  
  return numCategories > 1 || numMealPlans > 1 || dmcNumOptions > 1;
};

// =============================================================================
// HOTEL TABLE CONFIGURATION
// =============================================================================

/**
 * Standard columns for hotel tables
 */
export const HOTEL_TABLE_COLUMNS = [
  { key: 'destination', label: 'Destination', align: 'left' },
  { key: 'hotel', label: 'Hotel', align: 'left' },
  { key: 'room_type', label: 'Room Type', align: 'center' },
  { key: 'nights', label: 'Nights', align: 'center' },
  { key: 'meal', label: 'Meal', align: 'center' },
  { key: 'dates', label: 'Dates', align: 'center' }
];

/**
 * Meal plan badge colors
 */
export const MEAL_PLAN_COLORS = {
  AP: { bg: 'bg-green-100', text: 'text-green-700' },   // All meals included
  MAP: { bg: 'bg-blue-100', text: 'text-blue-700' },    // Breakfast + Dinner
  CP: { bg: 'bg-gray-100', text: 'text-gray-700' },     // Breakfast only
  EP: { bg: 'bg-gray-100', text: 'text-gray-500' },     // No meals
  default: { bg: 'bg-gray-100', text: 'text-gray-700' }
};

export const getMealPlanColor = (mealPlan) => {
  return MEAL_PLAN_COLORS[mealPlan] || MEAL_PLAN_COLORS.default;
};

// =============================================================================
// CURRENCY & FORMATTING
// =============================================================================

/**
 * Format currency in Indian format (₹1,23,456)
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format date in DD MMM format (e.g., "15 Sep")
 */
export const formatDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  } catch {
    return null;
  }
};

/**
 * Format date range (e.g., "15 Sep → 17 Sep")
 */
export const formatDateRange = (checkIn, checkOut) => {
  const formattedIn = formatDate(checkIn);
  const formattedOut = formatDate(checkOut);
  if (formattedIn && formattedOut) {
    return `${formattedIn} → ${formattedOut}`;
  }
  return formattedIn || formattedOut || '-';
};

// =============================================================================
// PAX CALCULATIONS
// =============================================================================

/**
 * Calculate pax breakdown from quotation data
 * 
 * @param {Object} quotation - Quotation object with room config
 * @returns {Object} - Pax breakdown
 */
export const calculatePaxBreakdown = (quotation) => {
  const noAdults = quotation.no_adults || 0;
  const noChildrenWithBed = quotation.no_children_with_bed || 0;
  const noChildrenNoBed = quotation.no_children_no_bed || 0;
  
  // Calculate twin sharing capacity
  const twinCapacity = (quotation.double_rooms || 0) * 2 + (quotation.twin_rooms || 0) * 2;
  const extraAdults = Math.max(0, noAdults - twinCapacity);
  const adultsInRooms = noAdults - extraAdults;
  
  return {
    totalAdults: noAdults,
    adultsInRooms,
    extraAdults,
    childrenWithBed: noChildrenWithBed,
    childrenNoBed: noChildrenNoBed,
    totalPax: noAdults + noChildrenWithBed + noChildrenNoBed
  };
};

// =============================================================================
// DATE CALCULATIONS FOR HOTELS
// =============================================================================

/**
 * Calculate check-in/check-out dates for a hotel in a sequence
 * 
 * @param {string} arrivalDate - Tour arrival date
 * @param {Array} hotels - Array of hotels in sequence
 * @param {number} hotelIndex - Index of current hotel
 * @returns {Object} - { checkIn, checkOut } formatted dates
 */
export const calculateHotelDates = (arrivalDate, hotels, hotelIndex) => {
  if (!arrivalDate) return { checkIn: null, checkOut: null };
  
  try {
    const baseDate = new Date(arrivalDate + 'T00:00:00');
    let cumulativeNights = 0;
    
    for (let i = 0; i < hotelIndex; i++) {
      cumulativeNights += hotels[i]?.nights || 1;
    }
    
    const checkInDate = new Date(baseDate);
    checkInDate.setDate(checkInDate.getDate() + cumulativeNights);
    
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkOutDate.getDate() + (hotels[hotelIndex]?.nights || 1));
    
    return {
      checkIn: checkInDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      checkOut: checkOutDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    };
  } catch {
    return { checkIn: null, checkOut: null };
  }
};

// =============================================================================
// COMPONENT VARIANTS
// =============================================================================

/**
 * Component size variants for different contexts
 */
export const COMPONENT_VARIANTS = {
  preview: {
    fontSize: 'text-xs',
    padding: 'p-2',
    headerBg: 'bg-[color-mix(in_srgb,var(--navy)_10%,white)]',
    headerText: 'text-[var(--navy)]',
    borderColor: 'border-[color-mix(in_srgb,var(--navy)_15%,transparent)]'
  },
  detail: {
    fontSize: 'text-sm',
    padding: 'p-2',
    headerBg: 'bg-[color-mix(in_srgb,var(--navy)_10%,white)]',
    headerText: 'text-[var(--navy)]',
    borderColor: 'border-gray-200'
  },
  pdf: {
    fontSize: 'text-xs',
    padding: 'p-1',
    headerBg: 'bg-[color-mix(in_srgb,var(--navy)_10%,white)]',
    headerText: 'text-[var(--navy)]',
    borderColor: 'border-gray-300'
  }
};

// =============================================================================
// ITINERARY TITLE FORMATTING (display-only)
// =============================================================================

/**
 * Inserts a visual separator between two phrases that got concatenated
 * without one, e.g. "Arrival at Cochin Transfer to Munnar" ->
 * "Arrival at Cochin → Transfer to Munnar". Display-only: never mutates
 * stored itinerary data, only the string shown on screen.
 */
export const formatItineraryTitle = (title) => {
  if (!title || typeof title !== 'string') return title;
  return title.replace(
    /([a-z0-9])\s+(Transfer to|Check-in at|Check-out from|Departure from|Drop at|Arrival at|Sightseeing in)/g,
    '$1 → $2'
  );
};

export default {
  PRICING_COLUMN_ORDER,
  PRICING_COLUMN_LABELS,
  getVisiblePricingColumns,
  shouldHideGrandTotal,
  HOTEL_TABLE_COLUMNS,
  MEAL_PLAN_COLORS,
  getMealPlanColor,
  formatCurrency,
  formatDate,
  formatDateRange,
  calculatePaxBreakdown,
  calculateHotelDates,
  COMPONENT_VARIANTS,
  formatItineraryTitle
};
